import React, { useEffect, useMemo, useState } from 'react';
import { LuCalendar, LuClock } from 'react-icons/lu';
import { MdAddShoppingCart, MdFlashOn } from 'react-icons/md';
import { useMediaQuery } from 'usehooks-ts';
import { ShopDTO } from '@/DTO/shopResponseDTO';
import ReactIcon, { default as Icon } from '@/components/ReactIcon';
import { DateUtils } from '@/helpers/dateUtils';
import {
    CartServiceType,
    CartValuesServiceStore,
    useShoppingCartStore,
} from '@/store/shoppingCart/shoppingCartStore';
import {
    AllSchedulesUnavailable,
    BadgeHour,
    BadgeWrapper,
    ButtonShop,
    CardShopWrapper,
    DetailsShopWrapper,
    HoursElementsText,
    UnavailableTippyContent,
    VideoScreen,
} from './styles';
import { Format } from '@/helpers/formatters';
import FirstHeatSvg from '@/assets/icons/first_heat.svg?react';
import { ButtonWithIcon } from '@/components/IconButton';
import { GiftDTO } from '@/DTO/authDTO';
import { useGiftsStore } from '@/hooks/useGiftsStore';
import Tippy from '@tippyjs/react';
import { followCursor } from 'tippy.js';
import { useUserProfile } from '@/hooks/useUserData';
import { ReservationDayEligibilityStatus } from '@/store/reservation/reservationStore';
import { WaveLevelEnum } from '@/helpers/utils';

type CardShopProps = {
    itemShop: ShopDTO;
    date: Date;
    handleAddToCart: (cartCurrentValue: CartValuesServiceStore, accessProfile: string) => void;
    isUndecidedDate?: boolean;
    isFirstHeat: boolean;
    currentGift?: GiftDTO;
    reservationDayStatus?: ReservationDayEligibilityStatus;
};

export const CardShop: React.FC<CardShopProps> = ({
    itemShop: {
        id,
        description,
        exhibitionName,
        schedules,
        waveSide,
        waveLevel,
        image,
        value,
    },
    date,
    handleAddToCart,
    isUndecidedDate,
    isFirstHeat,
    reservationDayStatus,
}) => {
    const matchQuery = useMediaQuery('(min-width: 768px)');
    const cartIsVisible = useShoppingCartStore(state => state.cartIsVisible);
    const giftsStore = useGiftsStore();
    const [currentHourId, setCurrentHourId] = useState(
        schedules.find(s => s.available > 0)?.id || 0
    );
    const user = useUserProfile();

    const isFreeSessionAvailable = useMemo(() => {
        if (!reservationDayStatus) return false;
        if (waveLevel === WaveLevelEnum.REEF) return reservationDayStatus.freeReefStatus === 'available';
        if (waveLevel === WaveLevelEnum.BAY) return reservationDayStatus.freeBayStatus === 'available';
        return false;
    }, [reservationDayStatus, waveLevel]);

    useEffect(() => {
        const availableHourIds = schedules.map(hour => hour.id);

        if (availableHourIds.length === 0) return;

        const firstAvailableSchedule = schedules.find(s => s.available > 0);
        setCurrentHourId(firstAvailableSchedule?.id || 0);
    }, [waveSide, schedules]);

    // if (availableSchedules.length === 0) return null;

    const onClick = (giftData?: { isFirstHeat: boolean; giftId: number }) => {
        const currentSchedule = schedules.find(
            sche => currentHourId === sche.id
        );

        handleAddToCart({
            id: String(id),
            title: exhibitionName,
            value: isFirstHeat ? 0 : isFreeSessionAvailable ? 0 : value,
            date,
            type: CartServiceType.Waves,
            scheduleId: currentHourId,
            scheduleHour: currentSchedule?.name,
            waveLevel,
            waveSide,
            available: currentSchedule?.available,
            freeSession: !isFirstHeat && isFreeSessionAvailable ? true : undefined,
            ...giftData,
        }, user.data?.accessProfile.name || "Guest" );
    };

    const handleAddGift = () => {
        giftsStore.update({
            isVisible: true,
            callback: gift => {
                if (gift) {
                    onClick({ giftId: gift.id, isFirstHeat: true });
                }
            },
        });
    };

    return (
        <CardShopWrapper>
            <VideoScreen src={image} alt={exhibitionName} />
            <DetailsShopWrapper>
                <h3>{exhibitionName}</h3>
                {matchQuery && !cartIsVisible && (
                    <span className="description" title={description}>
                        {description}
                    </span>
                )}

                {!isUndecidedDate && (
                    <BadgeWrapper>
                        {schedules.map((hour) => {
                            // Desativado por enquanto
                            // const isCurrentMostVisitted =
                            //     hour.id === hottestSchedule?.id;
                            const isCurrentMostVisitted = false;
                            const badgeHour = (
                                <BadgeHour
                                    key={hour.id}
                                    $isCurrent={currentHourId === hour.id}
                                    available={hour.available > 0}
                                    onClick={() => {
                                        if (hour.available > 0) {
                                            setCurrentHourId(hour.id);
                                        }
                                    }}
                                >
                                    {isCurrentMostVisitted && (
                                        <Icon Icon={MdFlashOn} size={'1rem'} />
                                    )}

                                    <span>{hour.name}</span>
                                </BadgeHour>
                            );
                            if (hour.available <= 0) {
                                return (
                                    <Tippy
                                        key={hour.id}
                                        content={
                                            <UnavailableTippyContent>
                                                <span>Horário esgotado</span>
                                            </UnavailableTippyContent>
                                        }
                                        delay={0}
                                        offset={[0, 20]}
                                        placement="right"
                                        followCursor={true}
                                        plugins={[followCursor]}
                                    >
                                        {badgeHour}
                                    </Tippy>
                                );
                            }
                            return (
                                <React.Fragment key={hour.id}>{badgeHour}</React.Fragment>
                            );
                        })}
                        {!schedules.some(
                            schedule => schedule.available > 0
                        ) && (
                            <AllSchedulesUnavailable>
                                <LuClock size={18} />
                                <span>Horários esgotados</span>
                            </AllSchedulesUnavailable>
                        )}
                    </BadgeWrapper>
                )}

                {!matchQuery && (
                    <section>
                        <ReactIcon Icon={LuCalendar} size={'1rem'} />
                        <HoursElementsText>
                            {DateUtils.formatPossibleDate(date)}
                        </HoursElementsText>
                    </section>
                )}
            </DetailsShopWrapper>

            {!isFirstHeat && !isFreeSessionAvailable && (
                <ButtonShop
                    onClick={() => {
                        onClick();
                    }}
                    disabled={!schedules.some(s => s.available > 0)}
                >
                    <Icon Icon={MdAddShoppingCart} size={'1.25rem'} />
                    {Format.currencyWithoutFraction(value)}
                </ButtonShop>
            )}
            {!isFirstHeat && isFreeSessionAvailable && (
                <ButtonWithIcon
                    variant="blue"
                    onClick={() => onClick()}
                    disabled={!schedules.some(s => s.available > 0)}
                >
                    <LuCalendar size={'1.25rem'} />
                    Resgatar
                </ButtonWithIcon>
            )}
            {isFirstHeat && (
                <ButtonWithIcon
                    variant="blue"
                    onClick={handleAddGift}
                    disabled={!schedules.some(s => s.available > 0)}
                >
                    <FirstHeatSvg />
                    Resgatar
                </ButtonWithIcon>
            )}
        </CardShopWrapper>
    );
};
